// frontend/src/api/papers.js
import api from "./axios";

/** 자료실 트리 조회 (백엔드: GET /api/papers/folders/tree?project=:id) */
export function getPapersTree(projectId) {
  const params = projectId ? { project: projectId } : undefined;
  return api.get("papers/folders/tree", { params });
}

/** (문서 기반) 파일 목록 조회
 * GET /api/papers/files/?project=:id&module=:name&dns=:dns
 * - module만 주면 모듈(=문서.module) 기준 집계
 */
export function getPapersFiles({
  project,
  module,
  dns,
  origin,
  include_deleted,
} = {}) {
  const params = {};
  if (project) params.project = project;
  if (module) params.module = module;
  if (dns) params.dns = dns;
  if (origin) params.origin = origin; // 'doc' | 'library'
  if (include_deleted != null) params.include_deleted = include_deleted; // 기본은 숨김
  return api.get("papers/files/", { params });
}

/** 폴더 생성 (POST) payload: { name, parent, project } */
export function createPapersFolder(payload) {
  return api.post("papers/folders/", payload);
}

/** 폴더 삭제 (DELETE /api/papers/folders/:id/) */
export function deletePapersFolder(id) {
  return api.delete(`papers/folders/${id}/`);
}

/** 파일 업로드 (자료실 직접 업로드) */ /** 파일 업로드 (자료실 직접 업로드) */
export function uploadPaperFile({
  project,
  folder,
  file,
  name,
  doc_dns,
  alertUserList,
  alertMessage,
}) {
  const form = new FormData();
  form.append("project", project);
  form.append("folder", folder);
  if (name) form.append("name", name);
  if (doc_dns) form.append("doc_dns", doc_dns); // ← 추가
  // 🔔 파일 공유 알림용 필드 (있을 때만 전송)
  if (alertUserList) form.append("alert_user_list", alertUserList);
  if (alertMessage) form.append("alert_message", alertMessage);
  form.append("file", file);
  return api.post("papers/files/", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

/** (문서에 파일 연결) POST /api/papers/files/:id/link/  body: { dns } */
export function linkFileToDoc(fileId, dns) {
  return api.post(`papers/files/${fileId}/link/`, { dns });
}

/** (문서에서 파일 연결 해제) POST /api/papers/files/:id/unlink/ body: { dns } */
export function unlinkFileFromDoc(fileId, dns) {
  return api.post(`papers/files/${fileId}/unlink/`, { dns });
}

/** 파일 상세 */
export function getPaperFile(id) {
  return api.get(`papers/files/${id}/`);
}

/** 다운로드 URL (a.href 등에 그대로 사용) */
export function getPaperDownloadUrl(id) {
  return `${api.defaults.baseURL}papers/files/${id}/download/`;
}

/** 파일 삭제 (DELETE /api/papers/files/:id/) */
export function deletePaperFile(id) {
  return api.delete(`papers/files/${id}/`);
}

export async function updateDocFileList(dns, finalIds) {
  // 1) 기존 연결 목록 조회
  const res = await api.get(`/papers/files/?dns=${dns}`);
  const oldFiles = Array.isArray(res.data?.results)
    ? res.data.results
    : res.data;

  const oldIds = oldFiles.map((f) => f.id);

  // diff 계산
  const toAdd = finalIds.filter((id) => !oldIds.includes(id));
  const toRemove = oldIds.filter((id) => !finalIds.includes(id));

  // 2) 삭제
  for (const id of toRemove) {
    await api.post(`/papers/files/${id}/unlink/`, { dns });
  }

  // 3) 추가
  for (const id of toAdd) {
    await api.post(`/papers/files/${id}/link/`, { dns });
  }

  return { added: toAdd, removed: toRemove };
}

/** Docs 소프트 삭제 (is_deleted=True or False)
 * PATCH /api/papers/docs/{dns}/soft-delete/
 */
export function softDeleteDoc(dns, isDeleted = true) {
  return api.patch(`papers/docs/${dns}/soft-delete/`, {
    is_deleted: isDeleted,
  });
}
/** 문서 첨부 업로드 또는 연결 */
export async function uploadDocAttachment(dns, payload) {
  // console.log("@@@@", payload);

  // ✅ (1) 로컬 업로드 → 새 파일 업로드
  if (payload.file) {
    const form = new FormData();
    form.append("file", payload.file);
    if (payload.name) form.append("name", payload.name);
    return api.post(`papers/docs/${dns}/attachments/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  // ✅ (2) 자료실 파일 연결 → JSON 전송으로 강제
  if (payload.file_ids) {
    // 숫자 배열로 보장
    const body = { file_ids: payload.file_ids.map((id) => Number(id)) };
    console.log("📡 보내는 데이터:", body);
    return api.post(`papers/docs/${dns}/attachments/`, body, {
      headers: { "Content-Type": "application/json" },
    });
  }

  throw new Error("Invalid payload for uploadDocAttachment");
}

export function getDocFiles(dns) {
  return api.get(`papers/docs/${dns}/files/`);
}

// ======= 문서 담당자 API =======
export async function getDocPersons(dns) {
  const res = await api.get("papers/doc-person/", { params: { dns: dns } });
  return res.data;
}

export async function updateDocPersons(dns, userIds = []) {
  const res = await api.post("papers/doc-person/", {
    dns,
    user_ids: userIds,
  });
  return res.data;
}
// ======= 담당자 기반 To-Do Summary =======
// 내가 담당자로 지정된 문서 중 state=save 인 문서 요약 + 전체 목록
export async function getMyTodoSummary() {
  const res = await api.get("papers/todo/my-summary/");
  return res.data;
}

/** 기존 자료실 파일에 대해 알림만 전송 */
export function notifyPaperFile(id, { alertUserList, alertMessage } = {}) {
  const body = {};
  if (alertUserList) body.alert_user_list = alertUserList;
  if (alertMessage) body.alert_message = alertMessage;
  return api.post(`papers/files/${id}/notify/`, body);
}

export async function updateDocStatus(dns, status) {
  try {
    const res = await api.patch(`/papers/docs/${dns}/status/`, { status });
    return res.data;
  } catch (err) {
    console.error("❌ updateDocStatus error:", err);
    throw err;
  }
}

export function getDocSignatureInfo(dns, project, userId) {
  if (!dns) {
    throw new Error("dns는 필수입니다.");
  }

  const params = { dns };

  if (project) {
    params.project = project;
  }

  if (userId) {
    params.user_id = userId; // ⭐ 작성자 / 현장대리인 구분 핵심
  }


  return api.get("papers/docs/signature-info/", {
    params,
  });
}

export function updatePaperFolderPermissions(folderId, { allowed_roles, allowed_users }) {
  return api.post(`papers/folders/${folderId}/permissions/`, {
    allowed_roles,
    allowed_users,
  });
}

export function updatePaperFilePermissions(fileId, { allowed_roles, allowed_users }) {
  return api.post(`papers/files/${fileId}/permissions/`, {
    allowed_roles,
    allowed_users,
  });
}

